import type { JSX } from 'react';

import { cn } from '@/lib/utils';

import type { AlertType } from '../types/alertsConfig';
import type { ProjectDetail } from '../ui/project-detail.types';
import AlertRuleEditor from './alerts-config/AlertRuleEditor';
import AlertsRulesList from './alerts-config/AlertsRulesList';
import useProjectAlertsConfigEditorState from './useProjectAlertsConfigEditorState';

interface ProjectAlertsConfigEditorContentProps {
  project: ProjectDetail;
  projectId: number;
  standardAlerts: AlertType[];
}

const ProjectAlertsConfigEditorContent = ({
  project,
  projectId,
  standardAlerts,
}: ProjectAlertsConfigEditorContentProps): JSX.Element => {
  const state = useProjectAlertsConfigEditorState({ project, standardAlerts });

  const {
    alertsConfigDraft,
    handleActionChange,
    handleActionParameterChange,
    handleAddAction,
    handleAddAlert,
    handleAddExample,
    handleAlertChange,
    handleDeleteAction,
    handleToggleStandardAlert,
    handleDeleteAlert,
    handleDeleteExample,
    handleExampleChange,
    handleSaveAlertsConfig,
    handleSelectAlert,
    handleTestAlerts,
    handleTestChange,
    isSaving,
    saveBanner,
    searchTerm,
    selectedAlert,
    selectedAlertIndex,
    selectedTestState,
    setSearchTerm,
  } = state;

  return (
    <div className="flex min-h-0 flex-col gap-3">
      {saveBanner ? (
        <p
          className={cn(
            'm-0 rounded-lg border px-3 py-2 text-sm font-body',
            saveBanner.variant === 'success'
              ? 'border-success/40 bg-success-light text-success'
              : 'border-error/40 bg-error-light text-error'
          )}
          role={saveBanner.variant === 'success' ? 'status' : 'alert'}
        >
          {saveBanner.text}
        </p>
      ) : null}

      <div className="grid min-h-0 overflow-hidden rounded-lg border border-border-subtle bg-bg-surface shadow-xs lg:grid-cols-[320px_minmax(0,1fr)]">
        <AlertsRulesList
          alerts={alertsConfigDraft}
          selectedAlertIndex={selectedAlertIndex}
          searchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          onSelectAlert={handleSelectAlert}
          onAddAlert={handleAddAlert}
        />
        <AlertRuleEditor
          alert={selectedAlert}
          alertIndex={selectedAlertIndex}
          projectId={projectId}
          testState={selectedTestState}
          isSaving={isSaving}
          handlers={{
            onAlertChange: handleAlertChange,
            onAddExample: handleAddExample,
            onExampleChange: handleExampleChange,
            onDeleteExample: handleDeleteExample,
            onAddAction: handleAddAction,
            onActionChange: handleActionChange,
            onActionParameterChange: handleActionParameterChange,
            onDeleteAction: handleDeleteAction,
            onToggleStandard: handleToggleStandardAlert,
            onDeleteAlert: (alertIndex) => {
              handleDeleteAlert(alertIndex).catch(() => {});
            },
            onTestChange: handleTestChange,
            onTestAlert: (alertIndex) => {
              handleTestAlerts(alertIndex).catch(() => {});
            },
            onSave: () => {
              handleSaveAlertsConfig().catch(() => {});
            },
          }}
        />
      </div>
    </div>
  );
};

export default ProjectAlertsConfigEditorContent;
